import { computed, type ComputedRef, inject, type InjectionKey, provide, ref, type Ref } from 'vue'
import type { FieldValidatorState } from './field-validator.composable'
import type { LValidationResult } from '@/libs/validation/components/LValidationForm.vue'

export const injectionSymbol = Symbol(
  'form-validator-injection'
) as InjectionKey<FormValidationInjection>

export type FormValidationResult = boolean | string

export type FormValidationRegisterFunctionParam = {
  id: string
  validate: (showErrorMessage?: boolean) => Promise<boolean>
  state: FieldValidatorState
}

export type FormValidationRegistrationEntry = {
  validate: (showErrorMessage?: boolean, stateful?: boolean) => Promise<boolean>
  state: FieldValidatorState
}

export type FormValidationRegistration = Record<string, FormValidationRegistrationEntry>

export type FormValidationInjection = {
  register: (entry: FormValidationRegisterFunctionParam) => void
  updateState: (id: string, state: { isValid: boolean; error?: string }) => void
  unregister: (id: string) => void
}

export const useInjectFormValidator = () => {
  const injection = inject<FormValidationInjection>(injectionSymbol)

  const register = (entry: FormValidationRegisterFunctionParam) => {
    if (!injection?.register) return
    injection.register(entry)
  }

  const updateState = (id: string, state: { isValid: boolean; error?: string }) => {
    if (!injection?.updateState) return
    injection.updateState(id, state)
  }

  const unregister = (id: string) => {
    if (!injection?.unregister) return
    injection.unregister(id)
  }

  return { register, updateState, unregister }
}

export const useFormValidator = () => {
  const validationFieldRegistration: Ref<FormValidationRegistration> = ref({})
  const _isValid: Ref<boolean | null> = ref(null)
  const isValid: ComputedRef<boolean | null> = computed(() => _isValid.value)
  const registeredFields: ComputedRef<string[]> = computed(() =>
    Object.keys(validationFieldRegistration.value).map((fieldId) => fieldId)
  )

  const register = ({ id, validate, state }: FormValidationRegisterFunctionParam) => {
    validationFieldRegistration.value[id] = { validate, state }
  }

  const unregister = (id: string) => {
    const hasEntry = validationFieldRegistration.value[id]
    if (!hasEntry) return
    delete validationFieldRegistration.value[id]
  }

  const updateState = (id: string, values: FieldValidatorState) => {
    const hasEntry = validationFieldRegistration.value[id]
    if (!hasEntry) return
    validationFieldRegistration.value[id].state.error = values.error
    validationFieldRegistration.value[id].state.isValid = values.isValid
    updateIsValid()
  }

  const updateIsValid = () => {
    _isValid.value = !Object.values(validationFieldRegistration.value).some(
      (entry) => !entry.state.isValid
    )
  }

  const validateForm = async (
    initial: boolean = false,
    stateful: boolean = true
  ): Promise<boolean> => {
    const validations = Object.values(validationFieldRegistration.value).map((field) =>
      field.validate(!initial, stateful)
    )

    return (await Promise.all(validations)).every((result) => result)
  }

  const hasErrorMessage = computed(() => {
    return Object.values(validationFieldRegistration.value).some(
      (entry) => !!entry.state.error && !entry.state.isValid
    )
  })

  const isPristine = computed(() => {
    return Object.values(validationFieldRegistration.value).every(
      (entry) => entry.state.isPristine === true
    )
  })

  const isDirty = computed(() => {
    return Object.values(validationFieldRegistration.value).some(
      (entry) => entry.state.isDirty === true
    )
  })

  const validationResult: ComputedRef<LValidationResult> = computed(() => ({
    isValid: _isValid.value ?? false,
    isPristine: isPristine.value,
    hasError: hasErrorMessage.value,
    isDirty: isDirty.value
  }))

  provide(injectionSymbol, { register, unregister, updateState })

  return {
    validateForm,
    validationResult,
    fields: registeredFields,
    hasErrorMessage,
    hasPristineFields: isPristine
  }
}
